import React from 'react';
import { Button as MuiButton, type ButtonProps as MuiButtonProps } from '@mui/material';
import type{ SxProps, Theme } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// Icon mapping - you can extend this with more icons
const iconMap: Record<string, React.ElementType> = {
  'cart': ShoppingCartIcon,
  'cart-plus': AddShoppingCartIcon,
  'plus': AddIcon,
  'minus': RemoveIcon,
  'delete': DeleteIcon,
  'edit': EditIcon,
  'check': CheckIcon,
  'close': CloseIcon,
};

// Size mapping for custom sizes
const sizeMap = {
  xs: { padding: '4px 8px', fontSize: '0.75rem', minWidth: '40px' },
  sm: { padding: '6px 12px', fontSize: '0.813rem', minWidth: '60px' },
  md: { padding: '8px 16px', fontSize: '0.875rem', minWidth: '80px' },
  lg: { padding: '10px 20px', fontSize: '0.938rem', minWidth: '100px' },
  xl: { padding: '12px 24px', fontSize: '1rem', minWidth: '120px' },
  '2xl': { padding: '14px 28px', fontSize: '1.125rem', minWidth: '140px' },
};

export interface CustomButtonProps extends Omit<MuiButtonProps, 'size' | 'color'> {
  // Color options
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning' | 'alert';
  
  // Size options (extended beyond MUI defaults)
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  
  // State
  state?: 'default' | 'hover' | 'active' | 'disabled' | 'loading';
  
  // Style variants
  outline?: boolean;
  
  // Icon configuration
  iconOnly?: boolean;
  showButtonText?: boolean;
  buttonText?: string;
  
  // Left icon
  showLeftIcon?: boolean;
  leftIconStyle?: string;
  
  // Right icon
  showRightIcon?: boolean;
  rightIconStyle?: string;
  
  // General icon style (fallback)
  iconStyle?: string;
  
  // Custom styling
  sx?: SxProps<Theme>;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  color = 'primary',
  size = 'md',
  state = 'default',
  outline = false,
  iconOnly = false,
  showButtonText = true,
  buttonText = 'Button',
  showLeftIcon = false,
  leftIconStyle = 'plus',
  showRightIcon = false,
  rightIconStyle = 'plus',
  iconStyle = 'plus',
  disabled,
  sx,
  children,
  ...rest
}) => {
  // Determine if button is disabled based on state or disabled prop
  const isDisabled = state === 'disabled' || disabled;
  const isLoading = state === 'loading';

  // Get icon components
  const LeftIconComponent = showLeftIcon 
    ? iconMap[leftIconStyle] || iconMap[iconStyle] || AddIcon
    : null;
  
  const RightIconComponent = showRightIcon 
    ? iconMap[rightIconStyle] || iconMap[iconStyle] || AddIcon
    : null;

  // Get size styles
  const sizeStyles = sizeMap[size] || sizeMap.md;

  // Determine button variant
  const variant = outline ? 'outlined' : 'contained';

  // Build custom styles
  const customStyles: SxProps<Theme> = {
    ...sizeStyles,
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: iconOnly ? 0 : '8px',
    justifyContent: 'center',
    ...(iconOnly && {
      minWidth: sizeStyles.minWidth,
      width: sizeStyles.minWidth,
      padding: sizeStyles.padding,
    }),
    ...(state === 'loading' && {
      pointerEvents: 'none',
      opacity: 0.7,
    }),
    ...sx,
  };

  // Render button content
  const renderContent = () => {
    if (iconOnly) {
      const IconComponent = LeftIconComponent || RightIconComponent || iconMap[iconStyle] || AddIcon;
      return <IconComponent />;
    }

    return (
      <>
        {LeftIconComponent && showLeftIcon && <LeftIconComponent />}
        {showButtonText && (children || buttonText)}
        {RightIconComponent && showRightIcon && <RightIconComponent />}
        {isLoading && <span style={{ marginLeft: '8px' }}>...</span>}
      </>
    );
  };

  return (
    <MuiButton
      variant={variant}
      color={color}
      disabled={isDisabled || isLoading}
      sx={customStyles}
      {...rest}
    >
      {renderContent()}
    </MuiButton>
  );
};

export default CustomButton;